import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import * as _ from 'lodash';
import { combineLatest, of } from 'rxjs';

import { UserService } from '../user.service';
import { AuthenticationModel } from '../../../authentication/authentication.model';
import { AuthenticatedUserService } from '../authenticatedUser';
import { error as loggingError } from 'helpers/monitoring/logging';

export const userGuard: CanActivateFn = (route, state) => {
    const router = inject(Router);
    const auth = inject(AuthService);
    const userService = inject(UserService);
    const authenticationModel = inject(AuthenticationModel);
    const authenticatedUserService = inject(AuthenticatedUserService);

    const idTokenClaimsDistinct$ = auth.idTokenClaims$.pipe(
        filter((idTokenClaims) => !!idTokenClaims),
        distinctUntilChanged((prev, curr) => prev.sub === curr.sub && prev.org_user_id === curr.org_user_id)
    );

    const orgUserFlow$ = idTokenClaimsDistinct$.pipe(map((idTokenClaims) => _.has(idTokenClaims, 'org_user_id')));

    return orgUserFlow$.pipe(
        switchMap((isOrgUserFlow) => {
            const isUserProfileLoaded = userService.isUserProfileLoaded() && userService.isLoggedIn();
            const skipUserProfileFetch = isUserProfileLoaded && !userService.hasUserSwitchedOrganization();

            if (!isOrgUserFlow || skipUserProfileFetch) {
                return of(true);
            }

            return combineLatest([
                authenticationModel.getEnhancedSigninInfo(),
                auth.user$.pipe(switchMap((user) => authenticationModel.getAppUserOrganizations(user.sub)))
            ]).pipe(
                tap(([enhancedSigninInfo, appUserOrganizations]) => {
                    const userInfo = userService.prepareEnhancedSigninInfo(enhancedSigninInfo);
                    authenticatedUserService.authenticate(userInfo);
                    authenticatedUserService.setUserOrganizations(appUserOrganizations);

                    userService.setUser();
                }),
                switchMap(([enhancedSigninInfo, appUserOrganizations]) => {
                    if (enhancedSigninInfo.user.blocked !== false) {
                        userService.removeUser();
                        if (appUserOrganizations.length > 1) {
                            return auth
                                .loginWithRedirect({
                                    appState: {
                                        switchOrgs: true
                                    }
                                })
                                .pipe(map(() => false));
                        } else {
                            return of(router.parseUrl('/logout'));
                        }
                    }

                    return of(router.parseUrl(state.url));
                })
            );
        }),
        catchError((error) => {
            loggingError('UserGuard: Critical error during user profile fetch or setup. Navigating to login.', error);
            return of(false);
        })
    );
};
